"use client";

import { truncateAddress } from "@aptos-labs/ts-sdk";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useSyncExternalStore } from "react";

import { toast } from "@/components/toast";
import { PENDING_REFERRAL_CODE_KEY } from "@/constants";
import { useDecibelWallet } from "@/contexts/decibel-wallet-provider";
import { useSubmitInvitationCode } from "@/contexts/mutations/referral/use-submit-invitation-code";
import {
  getParsedLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
  useLocalStorageSubscribe,
} from "@/hooks/storage";

export function setPendingReferralCode(code: string) {
  setLocalStorageItem(PENDING_REFERRAL_CODE_KEY, code);
}

export function removePendingReferralCode() {
  removeLocalStorageItem(PENDING_REFERRAL_CODE_KEY);
}

export function getPendingReferralCode() {
  const item = getParsedLocalStorageItem(PENDING_REFERRAL_CODE_KEY);
  if (!item || typeof item !== "string") return null;
  return item;
}

export const HandlePendingReferralCode = () => {
  const { accountAddress, isLoading: isLoadingWallet } = useDecibelWallet();
  const { mutateAsync: submitInvitationCode } = useSubmitInvitationCode();
  const generalT = useTranslations("general");
  const referralCodeT = useTranslations("referralCode");

  // Subscribe to the pending-code storage key so client-side navigation to
  // /r/<code> (which writes localStorage while this handler stays mounted in
  // RootLayout) re-triggers redemption without waiting for a wallet change or
  // a full reload.
  const pendingCode = useSyncExternalStore(
    useLocalStorageSubscribe,
    getPendingReferralCode,
    () => null,
  );

  // Only surface the "code applied / connect to continue" toast once per page load,
  // even if React re-renders this component while the wallet is disconnected.
  const hasShownPendingToast = useRef(false);

  useEffect(() => {
    if (!pendingCode) return;

    if (!accountAddress) {
      if (!isLoadingWallet && !hasShownPendingToast.current) {
        hasShownPendingToast.current = true;
        queueMicrotask(() => {
          toast({
            id: "referral-code-applied",
            variant: "info",
            title: referralCodeT("codeApplied"),
            description: referralCodeT("connectToContinue"),
          });
        });
      }
      return;
    }

    // Always attempt redemption once a wallet is connected. The backend handles
    // both first-time redemptions and orphan recovery (subaccount-already-exists,
    // no prior redemption, no trading/deposit activity) as 200; activity gate and
    // prior-different-code conflicts surface as 409 and bubble into the failure toast.
    submitInvitationCode({ code: pendingCode, account: accountAddress })
      .then(() => {
        queueMicrotask(() => {
          toast({
            id: "referral-code-redeemed",
            variant: "success",
            title: generalT("success"),
            description: referralCodeT("codeRedeemed"),
          });
        });
      })
      .catch((err: unknown) => {
        // Surface the backend's reason (e.g. activity gate, prior different-code
        // redemption) so the user understands whether to retry. Falls back to the
        // generic template for network or unknown errors.
        const reason = err instanceof Error && err.message ? err.message : null;
        queueMicrotask(() => {
          toast({
            title: generalT("error"),
            description:
              reason ??
              referralCodeT("redeemFailed", {
                code: pendingCode,
                wallet: truncateAddress(accountAddress),
              }),
            variant: "error",
          });
        });
      })
      .finally(() => {
        removePendingReferralCode();
      });
  }, [pendingCode, accountAddress, generalT, referralCodeT, isLoadingWallet, submitInvitationCode]);

  return null;
};
